import React, { useState, useEffect } from "react";
import { Text } from "../sudokit/components/Text";
import VStack from "../sudokit/Layout/VStack";

function ModelCreditMarkup() {
  const [inputCost, setInputCost] = useState("");
  const [outputCost, setOutputCost] = useState("");
  const [inputUnit, setInputUnit] = useState("1000"); // "1000" or "1MM"
  const [results, setResults] = useState(null);

  const calculateMarkup = () => {
    const inputCostNum = parseFloat(inputCost);
    const outputCostNum = parseFloat(outputCost);

    if (isNaN(inputCostNum) || isNaN(outputCostNum) || inputCostNum < 0 || outputCostNum < 0) {
      setResults(null);
      return;
    }

    // Convert inputs to cost per 1000 tokens (normalize)
    const inputCostPer1000 = inputUnit === "1000" ? inputCostNum : inputCostNum / 1000;
    const outputCostPer1000 = inputUnit === "1000" ? outputCostNum : outputCostNum / 1000;

    // Convert to cost per 1MM tokens for markup calculation
    const inputCostPer1MM = inputCostPer1000 * 1000;
    const outputCostPer1MM = outputCostPer1000 * 1000;
    const summedCost = inputCostPer1MM + outputCostPer1MM;

    // Calculate markup: MAX(40, MIN(300, -60 * ln(summed_cost) + 305)) / 100
    let markup = (-60 * Math.log(summedCost) + 305);
    markup = Math.max(40, Math.min(300, markup)) / 100;

    // Calculate credit costs (using per-1000 costs)
    const inputCreditCost = (inputCostPer1000 + inputCostPer1000 * markup) / 0.0000175;
    const outputCreditCost = (outputCostPer1000 + outputCostPer1000 * markup) / 0.0000175;

    setResults({
      inputCostPer1000,
      outputCostPer1000,
      inputCostPer1MM,
      outputCostPer1MM,
      summedCost,
      markup: markup * 100, // Convert to percentage for display
      inputCreditCost,
      outputCreditCost,
    });
  };

  useEffect(() => {
    if (inputCost && outputCost) {
      calculateMarkup();
    } else {
      setResults(null);
    }
  }, [inputCost, outputCost, inputUnit]);

  return (
    <div className="p-6 max-w-4xl">
      <Text className="text-2xl font-bold mb-6">Model Credit Markup Calculator</Text>

      <VStack spacing={6}>
        <div className="bg-app-menu-color-background p-4 rounded-lg border border-app-divider">
          <div className="flex justify-between items-center mb-4">
            <Text className="text-lg font-semibold">Input Costs</Text>
            <div className="flex rounded border border-app-grey-2 overflow-hidden">
              <button
                className={`px-3 py-1 text-sm font-medium text-app-grey-0 transition-colors ${
                  inputUnit === "1000"
                    ? "bg-app-grey-2"
                    : "bg-app-document-background"
                }`}
                onClick={() => setInputUnit("1000")}
              >
                per 1K tokens
              </button>
              <button
                className={`px-3 py-1 text-sm font-medium text-app-grey-0 transition-colors ${
                  inputUnit === "1MM"
                    ? "bg-app-grey-2"
                    : "bg-app-document-background"

                }`}
                onClick={() => setInputUnit("1MM")}
              >
                per 1MM tokens
              </button>
            </div>
          </div>

          <VStack spacing={4}>
            <div>
              <label className="block text-sm font-medium mb-2">
                Input Cost ($) - {inputUnit === "1000" ? "per 1,000 tokens" : "per 1MM tokens"}
              </label>
              <input
                type="number"
                step={inputUnit === "1000" ? "0.000001" : "0.01"}
                placeholder={inputUnit === "1000" ? "0.001500" : "1.500"}
                value={inputCost}
                onChange={(e) => setInputCost(e.target.value)}
                className="w-full px-3 py-2 bg-app-document-background border border-app-grey-2 rounded focus:outline-none focus:ring-1 focus:ring-app-selected-element-background"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Output Cost ($) - {inputUnit === "1000" ? "per 1,000 tokens" : "per 1MM tokens"}
              </label>
              <input
                type="number"
                step={inputUnit === "1000" ? "0.000001" : "0.01"}
                placeholder={inputUnit === "1000" ? "0.006000" : "6.000"}
                value={outputCost}
                onChange={(e) => setOutputCost(e.target.value)}
                className="w-full px-3 py-2 bg-app-document-background border border-app-grey-2 rounded focus:outline-none focus:ring-1 focus:ring-app-selected-element-background"
              />
            </div>
          </VStack>
        </div>

        {results && (
          <div className="bg-app-menu-color-background p-4 rounded-lg border border-app-divider">
            <Text className="text-lg font-semibold mb-4">Calculation Results</Text>

            <VStack spacing={3}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Text className="text-sm font-medium text-app-grey-0">Input Cost (1K tokens)</Text>
                  <Text className="text-lg">${results.inputCostPer1000.toFixed(6)}</Text>
                </div>

                <div>
                  <Text className="text-sm font-medium text-app-grey-0">Output Cost (1K tokens)</Text>
                  <Text className="text-lg">${results.outputCostPer1000.toFixed(6)}</Text>
                </div>

                <div>
                  <Text className="text-sm font-medium text-app-grey-0">Input Cost (1MM tokens)</Text>
                  <Text className="text-lg">${results.inputCostPer1MM.toFixed(2)}</Text>
                </div>

                <div>
                  <Text className="text-sm font-medium text-app-grey-0">Output Cost (1MM tokens)</Text>
                  <Text className="text-lg">${results.outputCostPer1MM.toFixed(2)}</Text>
                </div>
              </div>

              <hr className="border-app-divider my-2" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Text className="text-sm font-medium text-app-grey-0">Summed Cost (1MM tokens)</Text>
                  <Text className="text-lg">${results.summedCost.toFixed(2)}</Text>
                </div>

                <div>
                  <Text className="text-sm font-medium text-app-grey-0">Markup Percentage</Text>
                  <Text className="text-lg font-semibold text-green-400">{results.markup.toFixed(1)}%</Text>
                </div>
              </div>

              <hr className="border-app-divider my-2" />

              <div>
                <Text className="text-sm font-medium text-app-grey-0 mb-2">Credit Costs</Text>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-app-document-background p-3 rounded border">
                    <Text className="text-sm font-medium text-app-grey-0">Input Credits (per 1K tokens)</Text>
                    <Text className="text-xl font-bold text-blue-400">{results.inputCreditCost.toFixed(2)}</Text>
                  </div>

                  <div className="bg-app-document-background p-3 rounded border">
                    <Text className="text-sm font-medium text-app-grey-0">Output Credits (per 1K tokens)</Text>
                    <Text className="text-xl font-bold text-blue-400">{results.outputCreditCost.toFixed(2)}</Text>
                  </div>
                </div>
              </div>
            </VStack>
          </div>
        )}

        <div className="bg-app-menu-color-background p-4 rounded-lg border border-app-divider">
          <Text className="text-sm font-medium mb-2">Formula Details</Text>
          <VStack spacing={2}>
            <Text className="text-xs text-app-grey-0">Credit cost per 1,000 input tokens = (input_cost + (input_cost × markup)) / 0.0000175</Text>
            <Text className="text-xs text-app-grey-0">Credit cost per 1,000 output tokens = (output_cost + (output_cost × markup)) / 0.0000175</Text>
            <Text className="text-xs text-app-grey-0 mt-2">Where:</Text>
            <Text className="text-xs text-app-grey-0">• input_cost = cost of 1,000 input tokens</Text>
            <Text className="text-xs text-app-grey-0">• output_cost = cost of 1,000 output tokens</Text>
            <Text className="text-xs text-app-grey-0">• summed_cost = cost of 1MM input tokens + cost of 1MM output tokens</Text>
            <Text className="text-xs text-app-grey-0">• markup = MAX(40, MIN(300, -60 × ln(summed_cost) + 305)) / 100</Text>
            <Text className="text-xs text-app-grey-0 italic">Note: Markup has a minimum of 40% and maximum of 300%</Text>
          </VStack>
        </div>
      </VStack>
    </div>
  );
}

export default ModelCreditMarkup;
